import {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {
  FormTextField,
  TextField,
} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  ForgotPhoneNumberSendOtpPayload,
  useForgotPhoneNumberSendOtp,
} from '../requests/use-forgot-phone-number-send-otp';
import {useForgotPhoneNumberVerifyEmailOtp} from '../requests/use-forgot-phone-number-verify-email-otp';
import {useForgotPhoneNumberSendMobileOtp} from '../requests/use-forgot-phone-number-send-mobile-otp';
import {useForgotPhoneNumberVerifyMobileOtp} from '../requests/use-forgot-phone-number-verify-mobile-otp';

interface EmailCodeFormPayload {
  code: string;
}

interface MobileNumberFormPayload {
  mobile_number: string;
}

interface MobileCodeFormPayload {
  mobile_code: string;
}

type Step =
  | 'enter-email'
  | 'enter-email-code'
  | 'enter-mobile-number'
  | 'enter-mobile-code';

export function ForgotPhoneNumberPage() {
  const {trans} = useTrans();
  const isDarkMode = useIsDarkMode();
  const [step, setStep] = useState<Step>('enter-email');
  const [email, setEmail] = useState('');
  // the emailed code stays valid across steps, so it's carried along and
  // re-checked server-side on every subsequent request instead of being
  // consumed after the first check
  const [emailCode, setEmailCode] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');

  const emailForm = useForm<ForgotPhoneNumberSendOtpPayload>();
  const sendEmailOtp = useForgotPhoneNumberSendOtp();

  const emailCodeForm = useForm<EmailCodeFormPayload>();
  const verifyEmailOtp = useForgotPhoneNumberVerifyEmailOtp();

  const mobileNumberForm = useForm<MobileNumberFormPayload>();
  const sendMobileOtp = useForgotPhoneNumberSendMobileOtp();

  const mobileCodeForm = useForm<MobileCodeFormPayload>();
  const verifyMobileOtp = useForgotPhoneNumberVerifyMobileOtp();

  if (step === 'enter-email-code') {
    return (
      <AuthLayout>
        <StaticPageTitle>
          <Trans message="Forgot phone number" />
        </StaticPageTitle>
        <div className="mb-32 text-sm">
          <Trans
            message="Enter the verification code we sent to :email."
            values={{email}}
          />
        </div>
        <Form
          form={emailCodeForm}
          onSubmit={values => {
            verifyEmailOtp.mutate(
              {email, code: values.code},
              {
                onSuccess: () => {
                  setEmailCode(values.code);
                  setStep('enter-mobile-number');
                },
              },
            );
          }}
        >
          <Controller
            name="code"
            control={emailCodeForm.control}
            rules={{required: true}}
            render={({field: {onChange, ...field}, fieldState}) => (
              <TextField
                {...field}
                className="mb-24"
                onChange={e =>
                  onChange(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                invalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                label={<Trans message="Code" />}
                placeholder={trans(message('Enter your verification code'))}
                autoFocus
                autoComplete="one-time-code"
                autoCorrect="off"
                autoCapitalize="off"
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            )}
          />
          <Button
            className="block w-full"
            type="submit"
            variant="flat"
            color="primary"
            size="md"
            disabled={verifyEmailOtp.isPending}
          >
            <Trans message="Continue" />
          </Button>
          <Button
            variant="text"
            size="xs"
            className="mt-14"
            onClick={() => setStep('enter-email')}
          >
            <Trans message="Back" />
          </Button>
        </Form>
      </AuthLayout>
    );
  }

  if (step === 'enter-mobile-number') {
    return (
      <AuthLayout>
        <StaticPageTitle>
          <Trans message="Forgot phone number" />
        </StaticPageTitle>
        <div className="mb-32 text-sm">
          <Trans message="Enter the new mobile number you'd like to use to sign in." />
        </div>
        <Form
          form={mobileNumberForm}
          onSubmit={values => {
            sendMobileOtp.mutate(
              {email, code: emailCode, mobile_number: values.mobile_number},
              {
                onSuccess: () => {
                  setMobileNumber(values.mobile_number);
                  setStep('enter-mobile-code');
                },
              },
            );
          }}
        >
          <label
            className="mobile_label mb-24 block"
            style={{
              backgroundColor: isDarkMode ? 'rgb(29 29 33)' : 'white',
              color: isDarkMode ? 'white' : 'black',
            }}
          >
            <Trans message="New mobile number" />
            <Controller
              name="mobile_number"
              control={mobileNumberForm.control}
              rules={{required: true}}
              render={({field: {onChange, value}}) => (
                <div className="mobile_number w-full">
                  <ReactPhoneInput
                    value={value ?? ''}
                    onChange={phone => onChange(phone)}
                    country="in"
                    countryCodeEditable={false}
                    inputStyle={{
                      backgroundColor: isDarkMode ? 'rgb(29 29 33)' : 'white',
                      color: isDarkMode ? 'white' : 'black',
                      width: '100%',
                    }}
                    dropdownStyle={{
                      backgroundColor: isDarkMode ? 'rgb(29 29 33)' : 'white',
                      color: isDarkMode ? 'white' : 'black',
                    }}
                  />
                </div>
              )}
            />
          </label>
          <Button
            className="block w-full"
            type="submit"
            variant="flat"
            color="primary"
            size="md"
            disabled={sendMobileOtp.isPending}
          >
            <Trans message="Send code" />
          </Button>
          <Button
            variant="text"
            size="xs"
            className="mt-14"
            onClick={() => setStep('enter-email-code')}
          >
            <Trans message="Back" />
          </Button>
        </Form>
      </AuthLayout>
    );
  }

  if (step === 'enter-mobile-code') {
    return (
      <AuthLayout>
        <StaticPageTitle>
          <Trans message="Forgot phone number" />
        </StaticPageTitle>
        <div className="mb-32 text-sm">
          <Trans
            message="Enter the verification code we sent to :number."
            values={{number: `+${mobileNumber}`}}
          />
        </div>
        <Form
          form={mobileCodeForm}
          onSubmit={values => {
            verifyMobileOtp.mutate({
              email,
              code: emailCode,
              mobile_number: mobileNumber,
              mobile_code: values.mobile_code,
            });
          }}
        >
          <Controller
            name="mobile_code"
            control={mobileCodeForm.control}
            rules={{required: true}}
            render={({field: {onChange, ...field}, fieldState}) => (
              <TextField
                {...field}
                className="mb-24"
                onChange={e =>
                  onChange(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                invalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                label={<Trans message="Code" />}
                placeholder={trans(message('Enter your verification code'))}
                autoFocus
                autoComplete="one-time-code"
                autoCorrect="off"
                autoCapitalize="off"
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            )}
          />
          <Button
            className="block w-full"
            type="submit"
            variant="flat"
            color="primary"
            size="md"
            disabled={verifyMobileOtp.isPending}
          >
            <Trans message="Update phone number" />
          </Button>
          <Button
            variant="text"
            size="xs"
            className="mt-14"
            onClick={() => setStep('enter-mobile-number')}
          >
            <Trans message="Back" />
          </Button>
        </Form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <StaticPageTitle>
        <Trans message="Forgot phone number" />
      </StaticPageTitle>
      <Form
        form={emailForm}
        onSubmit={values => {
          sendEmailOtp.mutate(values, {
            onSuccess: () => {
              setEmail(values.email);
              setStep('enter-email-code');
            },
          });
        }}
      >
        <div className="mb-32 text-sm">
          <Trans message="Enter your account email and we'll send a verification code to confirm your new phone number." />
        </div>
        <FormTextField
          className="mb-32"
          name="email"
          type="email"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          label={<Trans message="Email" />}
          required
        />
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={sendEmailOtp.isPending}
        >
          <Trans message="Send code" />
        </Button>
      </Form>
    </AuthLayout>
  );
}
